import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Typography, IconButton,
  Stepper, Step, StepLabel,
  TextField, Autocomplete,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Checkbox, FormGroup,
  Alert, Divider, Paper, CircularProgress, Snackbar, Stack,
  Select, MenuItem, InputLabel,
} from '@mui/material';
import {
  ContentCopy, FileDownload, RestartAlt, ArrowBack, ArrowForward,
  AddCircleOutline, RemoveCircleOutline,
} from '@mui/icons-material';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import pharmacyApi from '../services/pharmacyApi';
import { useAuth } from '../contexts/AuthContext';
import { EncounterType } from '../types/pharmacy';
import type { WizardState, DrugEntry, DURFinding, ClinicalData, CounsellingItem } from '../types/pharmacy';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

const STEPS = [
  'Encounter Type',
  'Drug & Rx',
  'Patient Profile',
  'DUR & Safety',
  'Documentation',
  'Counselling',
  'DAP Output',
];

const SIG_OPTIONS = [
  '1 tablet by mouth once daily',
  '1 tablet by mouth twice daily',
  '1 tablet by mouth three times daily',
  '1 tablet by mouth four times daily',
  '1 tablet by mouth at bedtime',
  '2 tablets by mouth once daily',
  '1 capsule by mouth once daily',
  '1 capsule by mouth twice daily',
  '1/2 tablet by mouth once daily',
  '1-2 tablets by mouth every 4-6 hours as needed (max 8/day)',
  '1 tablet by mouth every morning with food',
  '5 mL by mouth twice daily',
  '10 mL by mouth three times daily',
  '1-2 puffs inhaled every 4-6 hours as needed',
  '2 puffs inhaled twice daily',
  'Apply a thin layer topically twice daily',
  'As directed by prescriber',
  'Custom — see notes',
];

// Auto-sig suggestions by dosage form / drug class
function inferSig(dosageForm: string, drugClass: string | null): string {
  const form = (dosageForm || '').toLowerCase();
  const cls  = (drugClass  || '').toLowerCase().replace(/_/g, ' ');
  if (form.includes('inhaler') || form.includes('inhale') || form.includes('puffer'))
    return '2 puffs inhaled twice daily';
  if (form.includes('cream') || form.includes('ointment') || form.includes('gel') || form.includes('lotion') || form.includes('topical'))
    return 'Apply a thin layer topically twice daily';
  if (form.includes('liquid') || form.includes('syrup') || form.includes('suspension') || form.includes('solution'))
    return '5 mL by mouth twice daily';
  if (form.includes('patch') || form.includes('transdermal'))
    return 'As directed by prescriber';
  if (cls.includes('insulin') || cls.includes('injectable') || form.includes('inject'))
    return 'As directed by prescriber';
  if (form.includes('capsule') || form.includes('cap'))
    return '1 capsule by mouth once daily';
  if (form.includes('tablet') || form.includes('tab'))
    return '1 tablet by mouth once daily';
  return '';
}

const AGE_RANGES = ['18-30', '31-45', '46-60', '61-75', '76+'];

const PATIENT_CONDITIONS = [
  'Hypertension',
  'Diabetes (Type 2)',
  'Diabetes (Type 1)',
  'Dyslipidemia',
  'Heart failure',
  'Atrial fibrillation',
  'Coronary artery disease',
  'COPD',
  'Asthma',
  'Chronic kidney disease',
  'Hepatic impairment',
  'Hypothyroidism',
  'Hyperthyroidism',
  'Osteoporosis',
  'Depression',
  'Anxiety',
  'Dementia',
  'Chronic pain',
  'Pregnancy',
  'Breastfeeding',
  'Bleeding disorder',
];

const DRUG_ALLERGY_CLASSES = [
  'Penicillins / Beta-lactams',
  'Cephalosporins',
  'Sulfonamides (Sulfa)',
  'NSAIDs',
  'ASA / Aspirin',
  'Opioids / Codeine',
  'ACE inhibitors',
  'Statins',
  'Fluoroquinolones',
  'Tetracyclines',
  'Macrolides',
  'Benzodiazepines',
  'Iodine / Contrast dye',
  'NKDA',
];

const ADAPTATION_TYPES = [
  'Dose change',
  'Formulation change (e.g. tablet to liquid)',
  'Route change',
  'Duration change',
  'Quantity change',
  'Frequency change',
  'Other',
];

const ADAPTATION_RATIONALES = [
  'Prescriber unavailable — minor technical adaptation required',
  'Dosage form unavailable — therapeutic equivalent dispensed',
  'Patient unable to swallow tablets — liquid formulation required',
  'Dose adjustment for renal impairment',
  'Dose adjustment for hepatic impairment',
  'Dose adjustment for age (elderly patient)',
  'Prescribing error corrected with prescriber notification',
  'Generic substitution — therapeutic equivalent',
];

const MINOR_AILMENTS = [
  'Uncomplicated urinary tract infection (UTI)',
  'Impetigo',
  'Cold sores (Herpes labialis)',
  'Mild to moderate acne',
  'Allergic rhinitis',
  'Mild eczema',
  'Oral contraception — continuation',
  'Dyslipidemia — continuation / renewal',
  'Hormonal contraception — continuation',
  'Travel medicine (malaria prophylaxis)',
  'Smoking cessation',
  'Emergency contraception',
];

const SYMPTOM_DURATIONS = ['< 24 hours', '1-3 days', '4-7 days', '1-2 weeks', '> 2 weeks'];

const PATIENT_UNDERSTANDING_OPTIONS = [
  'Patient verbalized understanding of instructions',
  'Patient declined counselling',
  'Language barrier — interpreter used',
  'Written handout provided — patient unable to discuss',
  'Caregiver counselled on behalf of patient',
];

const OUTCOME_OPTIONS = [
  'Dispensed as written',
  'Dispensed — adaptation made (see documentation)',
  'Prescription renewed — therapy continued',
  'Prescription transferred in',
  'Prescription transferred out',
  'Minor ailment prescription written',
  'Not dispensed — patient declined',
  'Not dispensed — clinical concern (see notes)',
  'Held pending prescriber clarification',
];

const FOLLOWUP_TIMEFRAMES = ['48-72 hours', '1 week', '2 weeks', '1 month', '3 months', 'PRN'];

// ─── STEP 5 FIELD CONFIG ─────────────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  type: 'select' | 'radio' | 'date' | 'text' | 'pharmacy-search';
  options?: string[];
  required?: boolean;
  showIf?: (d: Record<string, string>) => boolean;
}

const TYPE_FIELDS: Record<string, FieldDef[]> = {
  [EncounterType.NEW_DISPENSING]: [
    {
      key: 'indication',
      label: 'Indication / therapeutic use',
      type: 'select',
      options: [
        'Hypertension', 'Type 2 Diabetes', 'Type 1 Diabetes', 'Dyslipidemia',
        'Heart failure — reduced EF', 'Atrial fibrillation (rate/rhythm control)',
        'COPD — maintenance', 'Asthma — maintenance', 'Hypothyroidism',
        'Depression', 'Anxiety', 'Chronic pain management',
        'Anticoagulation (AF / VTE / mechanical valve)',
        'Osteoporosis', 'Acute infection', 'As directed by prescriber',
      ],
      required: true,
    },
    {
      key: 'doseWithinRange',
      label: 'Dose within approved therapeutic range?',
      type: 'radio',
      options: ['Yes', 'No — dose outside standard range'],
      required: true,
    },
    {
      key: 'doseAdjustRationale',
      label: 'Reason for non-standard dose',
      type: 'select',
      options: [
        'Dose titration in progress',
        'Dose adjusted for renal impairment',
        'Dose adjusted for hepatic impairment',
        'Dose adjusted for body weight',
        'Dose adjusted for age (elderly)',
        'As per specialist recommendation',
      ],
      showIf: (d) => d.doseWithinRange === 'No — dose outside standard range',
    },
  ],
  [EncounterType.REFILL]: [
    {
      key: 'adherence',
      label: 'Patient adherence assessment',
      type: 'radio',
      options: ['Adherent — consistent use', 'Partially adherent', 'Non-adherent'],
      required: true,
    },
    {
      key: 'refillsRemaining',
      label: 'Authorized refills remaining after this fill',
      type: 'select',
      options: ['0', '1', '2', '3', '4', '5', '6', '11 (annual supply)', 'As authorized'],
      required: true,
    },
    {
      key: 'clinicalChanges',
      label: 'Any new clinical changes since last fill?',
      type: 'radio',
      options: [
        'No changes reported',
        'New condition / medication added',
        'Adverse effect reported',
        'Lab values updated',
      ],
      required: true,
    },
  ],
  [EncounterType.RENEWAL]: [
    {
      key: 'therapyIndicated',
      label: 'Therapy still clinically indicated?',
      type: 'radio',
      options: [
        'Yes — continued benefit demonstrated',
        'Yes — with modified parameters',
        'No — therapy discontinued',
      ],
      required: true,
    },
    {
      key: 'continuationCriteria',
      label: 'Basis for renewal',
      type: 'select',
      options: [
        'Condition stable — ongoing maintenance therapy',
        'Clinical assessment confirms continued benefit',
        'Lab values within target range',
        'Patient reports symptom control',
        'Specialist recommends continuation',
        'ACP renewal criteria met',
      ],
      required: true,
    },
    {
      key: 'prescriberNotified',
      label: 'Prescriber notified of renewal?',
      type: 'radio',
      options: [
        'Yes — notified by fax',
        'Yes — notified by phone',
        'Not required (within scope)',
        'N/A',
      ],
      required: true,
    },
  ],
  [EncounterType.ADAPTATION]: [
    {
      key: 'adaptationType',
      label: 'Type of adaptation',
      type: 'select',
      options: ADAPTATION_TYPES,
      required: true,
    },
    {
      key: 'adaptationRationale',
      label: 'Clinical rationale (ACP s.16.3)',
      type: 'select',
      options: ADAPTATION_RATIONALES,
      required: true,
    },
    {
      key: 'prescriberNotified',
      label: 'Prescriber notification',
      type: 'radio',
      options: [
        'Yes — notified and agreed',
        'Yes — left message / awaiting callback',
        'Notification not feasible (urgent)',
      ],
      required: true,
    },
  ],
  [EncounterType.TRANSFER]: [
    {
      key: 'transferDirection',
      label: 'Transfer direction',
      type: 'radio',
      options: [
        'Transfer in (from another pharmacy)',
        'Transfer out (to another pharmacy)',
      ],
      required: true,
    },
    {
      key: 'originalPharmacy',
      label: 'Other pharmacy (name, city, or address)',
      type: 'pharmacy-search',
      required: true,
    },
    {
      key: 'originalSerialNumber',
      label: 'Original prescription serial number',
      type: 'text',
      required: true,
    },
    {
      key: 'refillsAtTransfer',
      label: 'Refills remaining at time of transfer',
      type: 'select',
      options: ['0', '1', '2', '3', '4', '5', 'Unknown'],
      required: true,
    },
  ],
  [EncounterType.PRESCRIBING]: [
    {
      key: 'minorAilment',
      label: 'Minor ailment assessed (ACP 2021 prescribing authority)',
      type: 'select',
      options: MINOR_AILMENTS,
      required: true,
    },
    {
      key: 'symptomDuration',
      label: 'Duration of symptoms',
      type: 'select',
      options: SYMPTOM_DURATIONS,
      required: true,
    },
    {
      key: 'prescribingBasis',
      label: 'Clinical basis for prescribing',
      type: 'select',
      options: [
        'Presentation consistent with minor ailment — no red flags identified',
        'Prior diagnosis confirmed — continuation therapy appropriate',
        'Symptom assessment supports self-limited condition',
        'Guidelines support pharmacist prescribing for this indication',
      ],
      required: true,
    },
    {
      key: 'monitoringPlan',
      label: 'Monitoring / follow-up plan',
      type: 'select',
      options: [
        'Reassess if no improvement in 3-5 days',
        'Return if symptoms worsen or new signs develop',
        'Follow-up in 1 week',
        'Refer to physician if condition persists',
        'No specific follow-up required for this indication',
      ],
      required: true,
    },
  ],
};

// ─── INITIAL STATE ────────────────────────────────────────────────────────────

const BLANK_DRUG_ENTRY: DrugEntry = {
  id: null,
  brandName: '',
  genericName: '',
  strength: '',
  dosageForm: '',
  route: '',
  din: '',
  drugClass: null,
  schedule: null,
  isHighAlert: false,
  isControlled: false,
  requiresTriplicate: false,
  qty: 30,
  daysSupply: 30,
  sig: '',
  genericSubAllowed: true,
  isManualDrug: false,
  clinicalData: null,
};

const INITIAL_STATE: WizardState = {
  encounterType: null,
  drugs: [],
  triplicateNumber: '',
  ageRange: '',
  sex: '',
  conditions: [],
  drugAllergies: [],
  durFindings: [],
  typeSpecificData: {},
  counsellingItems: [],
  patientUnderstanding: '',
  outcome: '',
  followUpRequired: false,
  followUpTimeframe: '',
  additionalNotes: '',
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function buildDURFindings(
  clinicalData: ClinicalData,
  drugAllergies: string[],
  drugIndex: number,
  drugName: string
): DURFinding[] {
  const findings: DURFinding[] = [];
  const prefix = drugName ? `[${drugName}] ` : '';
  const idPfx = `drug-${drugIndex}-`;
  const drugClassLower = (clinicalData.drug_class || '').toLowerCase().replace(/_/g, ' ');

  const allergyMatch = drugAllergies.find((a) => {
    if (a === 'NKDA') return false;
    const al = a.toLowerCase();
    if (al.includes('ace') && drugClassLower.includes('ace')) return true;
    if (al.includes('statin') && drugClassLower.includes('statin')) return true;
    if ((al.includes('penicillin') || al.includes('beta-lactam') || al.includes('beta lactam')) &&
        (drugClassLower.includes('penicillin') || drugClassLower.includes('beta'))) return true;
    const firstWord = al.split(' ')[0].split('/')[0].trim();
    return firstWord.length > 3 && drugClassLower.includes(firstWord);
  });

  if (allergyMatch) {
    findings.push({
      id: `${idPfx}allergy`,
      severity: 'CRITICAL',
      title: `${prefix}Potential Allergy Interaction`,
      message: `Patient has a listed allergy to "${allergyMatch}" — verify with patient before dispensing.`,
      acknowledged: false,
    });
  }

  if (clinicalData.is_high_alert) {
    findings.push({
      id: `${idPfx}high-alert`,
      severity: 'CRITICAL',
      title: `${prefix}High Alert Medication`,
      message: 'Independent double-check required. Verify dose, patient identity, and indication.',
      acknowledged: false,
    });
  }

  if (clinicalData.is_controlled) {
    findings.push({
      id: `${idPfx}controlled`,
      severity: 'CRITICAL',
      title: `${prefix}Controlled Substance`,
      message: 'Verify prescription authenticity and patient identification. Record triplicate number if Schedule I.',
      acknowledged: false,
    });
  }

  clinicalData.automation_rules.forEach((rule, i) => {
    findings.push({
      id: `${idPfx}rule-${i}`,
      severity: rule.action_type === 'COUNSELING' ? 'INFO' : 'WARNING',
      title: `${prefix}${rule.title}`,
      message: rule.message,
      acknowledged: false,
    });
  });

  clinicalData.required_checks.forEach((check, i) => {
    findings.push({
      id: `${idPfx}check-${i}`,
      severity: 'INFO',
      title: `${prefix}Required Check: ${check.label}`,
      message: `Target: ${check.target_range || 'See current clinical guidelines'}`,
      acknowledged: false,
    });
  });

  return findings;
}

function drugLabel(entry: DrugEntry, index: number): string {
  return entry.brandName || entry.genericName || `Drug ${index + 1}`;
}

function assembleDAPText(w: WizardState, pharmacistName: string, acpLicense: string): string {
  const today = new Date().toLocaleDateString('en-CA');
  const now = new Date().toLocaleString('en-CA');
  const multi = w.drugs.length > 1;

  const drugSections: string[] = w.drugs.map((entry, i) => {
    const label = multi ? `DRUG ${i + 1} OF ${w.drugs.length} — ` : 'DRUG INFORMATION\n  ';
    const nameLine = entry.isManualDrug
      ? `${entry.brandName} ${entry.strength}${entry.route ? ' — ' + entry.route : ''}`
      : [
          entry.brandName,
          entry.genericName ? `(${entry.genericName})` : '',
          entry.strength,
          entry.route ? '— ' + entry.route : '',
        ].filter(Boolean).join(' ');

    const meta = [
      entry.din ? `DIN: ${entry.din}` : null,
      entry.schedule ? `Schedule ${entry.schedule}` : null,
      entry.drugClass ? `Drug Class: ${entry.drugClass.replace(/_/g, ' ')}` : null,
    ].filter(Boolean).join('  |  ');

    const lines = [
      multi ? `${label}${nameLine}` : `DRUG INFORMATION\n  ${nameLine}`,
      meta ? `  ${meta}` : null,
      `  Qty: ${entry.qty} units  |  Days Supply: ${entry.daysSupply}`,
      `  Directions: ${entry.sig || 'As directed'}`,
      `  Generic substitution: ${entry.genericSubAllowed ? 'Permitted' : 'Not permitted'}`,
    ];
    return lines.filter(Boolean).join('\n');
  });

  const condList = w.conditions.length ? w.conditions.join(', ') : 'None reported';
  const allergyList = w.drugAllergies.length ? w.drugAllergies.join(', ') : 'NKDA';

  const durSection = w.durFindings.length === 0
    ? '  No DUR concerns identified.'
    : w.durFindings
        .map((f) => `  [${f.severity}] ${f.title}: ${f.message}${f.acknowledged ? ' [ACKNOWLEDGED]' : ''}`)
        .join('\n');

  const fields = w.encounterType ? (TYPE_FIELDS[w.encounterType] || []) : [];
  const typeSection = fields.length === 0
    ? '  N/A'
    : fields
        .filter((fd) => !fd.showIf || fd.showIf(w.typeSpecificData))
        .map((fd) => `  ${fd.label}: ${w.typeSpecificData[fd.key] || 'Not specified'}`)
        .join('\n');

  const counselledItems = w.counsellingItems.filter((c) => c.checked);
  const counselSection = counselledItems.length
    ? counselledItems.map((c) => `  \u2022 ${c.item}`).join('\n')
    : '  No counselling items recorded.';

  const unchecked = w.counsellingItems
    .filter((c) => c.required && !c.checked)
    .map((c) => `  [NOT COVERED \u2014 required] ${c.item}`)
    .join('\n');

  const followUp = w.followUpRequired
    ? (w.followUpTimeframe || 'As needed')
    : 'None required';

  const lines = [
    'PHARMACIST CLINICAL DOCUMENTATION',
    `Encounter Type: ${w.encounterType || 'Not specified'}`,
    `Date: ${today}  |  ACP License: ${acpLicense || 'See signature'}`,
    '',
    ...drugSections.flatMap((s, i) => i < drugSections.length - 1 ? [s, ''] : [s]),
    '',
    'PATIENT PROFILE (Anonymous)',
    `  Age range: ${w.ageRange || 'Not recorded'}  |  Sex: ${w.sex || 'Not recorded'}`,
    `  Conditions: ${condList}`,
    `  Known drug allergies: ${allergyList}`,
    '',
    'DRUG UTILIZATION REVIEW (DUR)',
    durSection,
    '',
    `${(w.encounterType || 'ENCOUNTER').toUpperCase()} \u2014 DOCUMENTATION`,
    typeSection,
    '',
    'COUNSELLING PROVIDED',
    counselSection,
    unchecked || null,
    `  Patient understanding: ${w.patientUnderstanding || 'Not recorded'}`,
    '',
    'DISPOSITION & OUTCOME',
    `  Outcome: ${w.outcome || 'Not recorded'}`,
    `  Follow-up: ${followUp}`,
    '',
    'ADDITIONAL NOTES',
    `  ${w.additionalNotes || 'None.'}`,
    '',
    '='.repeat(60),
    `Pharmacist: ${pharmacistName}  |  ACP License: ${acpLicense || 'N/A'}  |  Generated: ${now}`,
  ].filter((l) => l !== null) as string[];

  return lines.join('\n');
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

const PrescriptionProcessing: React.FC = () => {
  const { state: authState } = useAuth();
  const pharmacistName: string = (authState.user as any)?.full_name || 'Pharmacist';
  const acpLicense: string = (authState.user as any)?.acp_license_number || '';

  const [activeStep, setActiveStep] = useState(0);
  const [wizard, setWizard] = useState<WizardState>({ ...INITIAL_STATE });
  const [addSearch, setAddSearch] = useState({ query: '', options: [] as any[] });
  const [durLoading, setDurLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', color: 'success' as 'success' | 'error' });
  const [pharmSearch, setPharmSearch] = useState<{ options: any[]; loading: boolean }>({ options: [], loading: false });
  const pharmSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const update = useCallback((partial: Partial<WizardState>) => {
    setWizard((prev) => ({ ...prev, ...partial }));
  }, []);

  // ── Single add-drug search ────────────────────────────────────────────────
  const handleAddQuery = useCallback((query: string) => {
    setAddSearch((prev) => ({ ...prev, query }));
    if (addSearchTimer.current) clearTimeout(addSearchTimer.current);
    if (query.length < 2) {
      setAddSearch((prev) => ({ ...prev, options: [] }));
      return;
    }
    addSearchTimer.current = setTimeout(async () => {
      try {
        const results = await pharmacyApi.searchMedications(query);
        setAddSearch((prev) => ({ ...prev, options: Array.isArray(results) ? results : [] }));
      } catch {
        setAddSearch((prev) => ({ ...prev, options: [] }));
      }
    }, 300);
  }, []);

  const addDrugFromSelection = useCallback((selected: any) => {
    const dosageForm = selected.dosage_form || '';
    const drugClass  = selected.drug_class  || null;
    const entry: DrugEntry = {
      ...BLANK_DRUG_ENTRY,
      id: selected.id,
      brandName: selected.brand_name || '',
      genericName: selected.generic_name || '',
      strength: selected.strength || '',
      dosageForm,
      route: selected.route || '',
      din: selected.din || '',
      drugClass,
      schedule: selected.drug_schedule || null,
      isHighAlert: !!selected.is_high_alert,
      isControlled: !!selected.is_controlled,
      requiresTriplicate: !!selected.requires_triplicate,
      sig: inferSig(dosageForm, drugClass),
    };
    setWizard((prev) => ({ ...prev, drugs: [...prev.drugs, entry], durFindings: [], counsellingItems: [] }));
    setAddSearch({ query: '', options: [] });
  }, []);

  const handlePharmSearch = useCallback((query: string, setVal: (v: string) => void) => {
    setVal(query);
    if (pharmSearchTimer.current) clearTimeout(pharmSearchTimer.current);
    if (query.length < 2) {
      setPharmSearch({ options: [], loading: false });
      return;
    }
    setPharmSearch((prev) => ({ ...prev, loading: true }));
    pharmSearchTimer.current = setTimeout(async () => {
      try {
        const results = await pharmacyApi.searchPharmacies(query, 20);
        setPharmSearch({ options: Array.isArray(results) ? results : [], loading: false });
      } catch {
        setPharmSearch({ options: [], loading: false });
      }
    }, 300);
  }, []);

  const updateDrugEntry = useCallback((index: number, patch: Partial<DrugEntry>) => {
    setWizard((prev) => {
      const drugs = prev.drugs.map((d, i) => i === index ? { ...d, ...patch } : d);
      return { ...prev, drugs, durFindings: [], counsellingItems: [] };
    });
  }, []);

  const addManualDrug = () => {
    setWizard((prev) => ({
      ...prev,
      drugs: [...prev.drugs, { ...BLANK_DRUG_ENTRY, isManualDrug: true }],
      durFindings: [],
      counsellingItems: [],
    }));
  };

  const removeDrug = (index: number) => {
    setWizard((prev) => ({
      ...prev,
      drugs: prev.drugs.filter((_, i) => i !== index),
      durFindings: [],
      counsellingItems: [],
    }));
  };

  // ── DUR: load clinical data for all drugs on entering step 4 ─────────────
  useEffect(() => {
    if (activeStep !== 3) return;
    (async () => {
      setDurLoading(true);
      try {
        const updatedDrugs = wizard.drugs.map((d) => ({ ...d }));
        const allFindings: DURFinding[] = [];
        const counselSeen = new Set<string>();
        const allCounselling: CounsellingItem[] = [];

        await Promise.all(
          wizard.drugs.map(async (entry, i) => {
            if (!entry.id) return; // manual entry — no API DUR
            try {
              const cd: ClinicalData = await pharmacyApi.getMedicationClinicalData(entry.id);
              updatedDrugs[i] = { ...updatedDrugs[i], clinicalData: cd };
              const label = drugLabel(entry, i);
              allFindings.push(...buildDURFindings(cd, wizard.drugAllergies, i, label));
              cd.counselling_items.forEach((c: CounsellingItem) => {
                if (!counselSeen.has(c.item)) {
                  counselSeen.add(c.item);
                  allCounselling.push({ ...c, checked: false });
                }
              });
            } catch {
              // API error or drug not in DB — skip DUR for this entry
            }
          })
        );

        update({ drugs: updatedDrugs, durFindings: allFindings, counsellingItems: allCounselling });
      } finally {
        setDurLoading(false);
      }
    })();
  }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Rebuild DUR findings locally when allergies change ───────────────────
  useEffect(() => {
    setWizard((prev) => {
      const findings: DURFinding[] = [];
      prev.drugs.forEach((entry, i) => {
        if (entry.clinicalData) {
          findings.push(...buildDURFindings(entry.clinicalData, prev.drugAllergies, i, drugLabel(entry, i)));
        }
      });
      return { ...prev, durFindings: findings };
    });
  }, [wizard.drugAllergies]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step validation ───────────────────────────────────────────────────────
  const canAdvance = (): boolean => {
    switch (activeStep) {
      case 0:
        return !!wizard.encounterType;
      case 1:
        return wizard.drugs.length > 0 && wizard.drugs.every((entry) => {
          const drugOk = entry.isManualDrug ? !!entry.brandName && !!entry.strength : !!entry.id;
          const tppOk = !entry.requiresTriplicate || !!wizard.triplicateNumber;
          return drugOk && entry.qty > 0 && entry.daysSupply > 0 && !!entry.sig && tppOk;
        });
      case 2:
        return true; // age range & sex are optional
      case 3:
        return wizard.durFindings.filter((f) => f.severity === 'CRITICAL').every((f) => f.acknowledged);
      case 4: {
        if (!wizard.encounterType) return false;
        const fields = TYPE_FIELDS[wizard.encounterType] || [];
        return fields
          .filter((fd) => fd.required && (!fd.showIf || fd.showIf(wizard.typeSpecificData)))
          .every((fd) => !!wizard.typeSpecificData[fd.key]);
      }
      case 5:
        return !!wizard.patientUnderstanding && !!wizard.outcome;
      default:
        return true;
    }
  };

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);
  const handleReset = () => {
    setActiveStep(0);
    setWizard({ ...INITIAL_STATE });
    setAddSearch({ query: '', options: [] });
  };

  // ── DAP actions ───────────────────────────────────────────────────────────
  const dapText = assembleDAPText(wizard, pharmacistName, acpLicense);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dapText);
      setSnackbar({ open: true, msg: 'DAP copied to clipboard', color: 'success' });
    } catch {
      setSnackbar({ open: true, msg: 'Copy failed — select text manually', color: 'error' });
    }
  };

  const handleDownload = async () => {
    const paragraphs = dapText.split('\n').map(
      (line) => new Paragraph({
        children: [new TextRun({ text: line, font: 'Courier New', size: 20 })],
        spacing: { after: 0 },
      })
    );
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Pharmacist Clinical Documentation — DAP', bold: true, size: 28, font: 'Calibri' })],
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
          }),
          ...paragraphs,
        ],
      }],
    });
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const enc = (wizard.encounterType || 'Document').replace(/\s/g, '-');
    a.download = `DAP-${enc}-${new Date().toISOString().slice(0, 10)}.docx`;
    a.click();
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, msg: 'DAP downloaded as .docx', color: 'success' });
  };

  // ── Step renderers ────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Select Encounter Type</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Determines which documentation fields appear in Step 5.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {Object.values(EncounterType).map((et) => (
          <Card
            key={et}
            variant={wizard.encounterType === et ? 'elevation' : 'outlined'}
            elevation={wizard.encounterType === et ? 6 : 0}
            onClick={() => update({ encounterType: et as WizardState['encounterType'] })}
            sx={{
              cursor: 'pointer',
              border: wizard.encounterType === et ? '2px solid #005EB8' : '1px solid rgba(0,0,0,0.12)',
              backgroundColor: wizard.encounterType === et ? '#e8f0fe' : 'background.paper',
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#005EB8', backgroundColor: '#f0f4ff' },
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={wizard.encounterType === et ? 700 : 400}>
                {et}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Drug & Prescription Details</Typography>

      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          options={addSearch.options}
          getOptionLabel={(o: any) =>
            `${o.brand_name || o.generic_name || ''}${o.strength ? ' ' + o.strength : ''}${o.din ? ' — DIN: ' + o.din : ''}`
          }
          filterOptions={(x) => x}
          inputValue={addSearch.query}
          value={null}
          onInputChange={(_, v, reason) => { if (reason !== 'reset') handleAddQuery(v); }}
          onChange={(_, selected: any) => { if (selected) addDrugFromSelection(selected); }}
          renderInput={(params) => (
            <TextField {...params} label="Search drug to add (name or DIN)" size="small" fullWidth
              placeholder="Type drug name or DIN, select to add…" />
          )}
          noOptionsText={addSearch.query.length < 2 ? 'Type at least 2 characters…' : 'No medications found'}
          blurOnSelect
        />
        <Button
          variant="outlined" size="small" startIcon={<AddCircleOutline />}
          onClick={addManualDrug}
          sx={{ whiteSpace: 'nowrap', height: 40, mt: 0.25 }}
        >
          Add manually
        </Button>
      </Stack>

      {wizard.drugs.length === 0 && (
        <Alert severity="info">Search for a drug above or click "Add manually" to get started.</Alert>
      )}

      {wizard.drugs.map((entry, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>

          {/* Drug identity line */}
          {entry.isManualDrug ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 1, mb: 1.5, alignItems: 'center' }}>
              <TextField label="Drug name" size="small" required
                value={entry.brandName}
                onChange={(e) => updateDrugEntry(index, { brandName: e.target.value })} />
              <TextField label="Strength" size="small" required
                value={entry.strength}
                onChange={(e) => updateDrugEntry(index, { strength: e.target.value })} />
              <TextField label="Route" size="small"
                value={entry.route}
                onChange={(e) => updateDrugEntry(index, { route: e.target.value })} />
              <TextField label="DIN (if known)" size="small"
                value={entry.din}
                onChange={(e) => updateDrugEntry(index, { din: e.target.value })} />
              <IconButton size="small" color="error" onClick={() => removeDrug(index)}>
                <RemoveCircleOutline fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                {wizard.drugs.length > 1 && (
                  <Typography variant="caption" color="text.secondary">#{index + 1}</Typography>
                )}
                <Typography variant="body2" fontWeight={600}>
                  {entry.brandName}{entry.genericName ? ` (${entry.genericName})` : ''}{entry.strength ? ` ${entry.strength}` : ''}
                </Typography>
                {entry.din && (
                  <Typography variant="caption" color="text.secondary">DIN: {entry.din}</Typography>
                )}
                {entry.schedule && (
                  <Chip label={`Sch. ${entry.schedule}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                {entry.drugClass && (
                  <Chip label={entry.drugClass.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                {entry.isHighAlert && (
                  <Chip label="HIGH ALERT" color="error" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                {entry.isControlled && (
                  <Chip label="CONTROLLED" color="warning" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
              </Stack>
              <IconButton size="small" color="error" onClick={() => removeDrug(index)} sx={{ ml: 1, flexShrink: 0 }}>
                <RemoveCircleOutline fontSize="small" />
              </IconButton>
            </Stack>
          )}

          {/* Rx fields */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '72px 72px 1fr auto', gap: 1, alignItems: 'center' }}>
            <TextField label="Qty" type="number" size="small"
              value={entry.qty}
              onChange={(e) => updateDrugEntry(index, { qty: Math.max(1, parseInt(e.target.value) || 1) })}
              inputProps={{ min: 1 }} />
            <TextField label="Days" type="number" size="small"
              value={entry.daysSupply}
              onChange={(e) => updateDrugEntry(index, { daysSupply: Math.max(1, parseInt(e.target.value) || 1) })}
              inputProps={{ min: 1 }} />
            <FormControl fullWidth size="small">
              <InputLabel>Sig (Directions)</InputLabel>
              <Select value={entry.sig} label="Sig (Directions)"
                onChange={(e) => updateDrugEntry(index, { sig: e.target.value })}>
                {SIG_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Generic sub:
              </Typography>
              <Button size="small" variant={entry.genericSubAllowed ? 'contained' : 'outlined'} color="success"
                sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                onClick={() => updateDrugEntry(index, { genericSubAllowed: true })}>✓</Button>
              <Button size="small" variant={!entry.genericSubAllowed ? 'contained' : 'outlined'} color="error"
                sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                onClick={() => updateDrugEntry(index, { genericSubAllowed: false })}>✗</Button>
            </Stack>
          </Box>

          {entry.requiresTriplicate && (
            <Chip label="Requires TPP Triplicate" color="warning" size="small" sx={{ mt: 1, fontSize: '0.75rem' }} />
          )}
        </Paper>
      ))}

      {wizard.drugs.some((entry) => entry.requiresTriplicate) && (
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Triplicate Prescription Number (required — Schedule I)"
            value={wizard.triplicateNumber}
            onChange={(e) => update({ triplicateNumber: e.target.value })}
            fullWidth size="small"
            required
            error={!wizard.triplicateNumber}
            helperText={!wizard.triplicateNumber ? 'Required for Schedule I narcotics (Alberta TPP)' : ''}
          />
        </Box>
      )}
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Patient Profile</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        No identifying information required. Used for DUR allergy check and DAP context only.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Age Range</InputLabel>
          <Select value={wizard.ageRange} label="Age Range" onChange={(e) => update({ ageRange: e.target.value })}>
            {AGE_RANGES.map((a) => <MenuItem key={a} value={a}>{a} years</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl component="fieldset">
          <FormLabel>Sex</FormLabel>
          <RadioGroup row value={wizard.sex} onChange={(e) => update({ sex: e.target.value })}>
            {['Male', 'Female', 'Other'].map((s) => (
              <FormControlLabel key={s} value={s} control={<Radio />} label={s} />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" gutterBottom>Relevant conditions</Typography>
      <FormGroup sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', mb: 2 }}>
        {PATIENT_CONDITIONS.map((c) => (
          <FormControlLabel
            key={c}
            control={
              <Checkbox
                size="small"
                checked={wizard.conditions.includes(c)}
                onChange={(e) =>
                  update({ conditions: e.target.checked ? [...wizard.conditions, c] : wizard.conditions.filter((x) => x !== c) })
                }
              />
            }
            label={<Typography variant="body2">{c}</Typography>}
          />
        ))}
      </FormGroup>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" gutterBottom>Known drug allergies / intolerances</Typography>
      <FormGroup sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {DRUG_ALLERGY_CLASSES.map((a) => (
          <FormControlLabel
            key={a}
            control={
              <Checkbox
                size="small"
                checked={wizard.drugAllergies.includes(a)}
                onChange={(e) => {
                  let next = e.target.checked
                    ? [...wizard.drugAllergies, a]
                    : wizard.drugAllergies.filter((x) => x !== a);
                  if (a === 'NKDA' && e.target.checked) next = ['NKDA'];
                  update({ drugAllergies: next });
                }}
              />
            }
            label={<Typography variant="body2">{a}</Typography>}
          />
        ))}
      </FormGroup>
    </Box>
  );

  const renderStep4 = () => {
    if (durLoading) {
      return (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Running DUR check for {wizard.drugs.length} drug{wizard.drugs.length > 1 ? 's' : ''}...
          </Typography>
        </Box>
      );
    }

    const ackFinding = (id: string, ack: boolean) =>
      setWizard((prev) => ({
        ...prev,
        durFindings: prev.durFindings.map((f) => (f.id === id ? { ...f, acknowledged: ack } : f)),
      }));

    const criticals = wizard.durFindings.filter((f) => f.severity === 'CRITICAL');
    const warnings = wizard.durFindings.filter((f) => f.severity === 'WARNING');
    const infos = wizard.durFindings.filter((f) => f.severity === 'INFO');
    const manualEntries = wizard.drugs.filter((d) => !d.id);

    return (
      <Box>
        <Typography variant="h6" gutterBottom>DUR & Safety Review</Typography>

        {wizard.durFindings.length === 0 && !manualEntries.length && (
          <Alert severity="success" sx={{ mb: 1 }}>
            No DUR concerns identified for {wizard.drugs.length > 1 ? 'all drugs' : 'this drug'} and patient profile.
          </Alert>
        )}

        {criticals.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
              Critical — acknowledge each to proceed
            </Typography>
            {criticals.map((f) => (
              <Alert key={f.id} severity="error" sx={{ mb: 1 }}
                action={
                  <Checkbox checked={f.acknowledged} onChange={(e) => ackFinding(f.id, e.target.checked)} color="error" />
                }
              >
                <strong>{f.title}</strong><br />{f.message}
              </Alert>
            ))}
          </Box>
        )}

        {warnings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>Warnings</Typography>
            {warnings.map((f) => (
              <Alert key={f.id} severity="warning" sx={{ mb: 1 }}
                action={
                  <Checkbox checked={f.acknowledged} onChange={(e) => ackFinding(f.id, e.target.checked)} color="warning" />
                }
              >
                <strong>{f.title}</strong><br />{f.message}
              </Alert>
            ))}
          </Box>
        )}

        {infos.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="info.main" sx={{ mb: 1 }}>Lab & Monitoring Requirements</Typography>
            {infos.map((f) => (
              <Alert key={f.id} severity="info" sx={{ mb: 1 }}>
                <strong>{f.title}</strong> — {f.message}
              </Alert>
            ))}
          </Box>
        )}

        {manualEntries.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {manualEntries.length === 1 ? 'One drug entry' : `${manualEntries.length} drug entries`} added manually
            (not linked to database) — automated DUR unavailable for {manualEntries.length > 1 ? 'these drugs' : 'this drug'}.
            Review manually before proceeding.
          </Alert>
        )}
      </Box>
    );
  };

  const renderStep5 = () => {
    if (!wizard.encounterType) return null;
    const fields = TYPE_FIELDS[wizard.encounterType] || [];

    return (
      <Box>
        <Typography variant="h6" gutterBottom>{wizard.encounterType} — Documentation</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          ACP-required fields for this encounter type. Applied to the encounter as a whole.
        </Typography>
        <Stack spacing={3}>
          {fields
            .filter((fd) => !fd.showIf || fd.showIf(wizard.typeSpecificData))
            .map((fd) => {
              const val = wizard.typeSpecificData[fd.key] || '';
              const setVal = (v: string) =>
                update({ typeSpecificData: { ...wizard.typeSpecificData, [fd.key]: v } });

              if (fd.type === 'radio') {
                return (
                  <FormControl key={fd.key} component="fieldset">
                    <FormLabel required={fd.required}>{fd.label}</FormLabel>
                    <RadioGroup value={val} onChange={(e) => setVal(e.target.value)} sx={{ mt: 0.5 }}>
                      {fd.options!.map((o) => (
                        <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />
                      ))}
                    </RadioGroup>
                  </FormControl>
                );
              }
              if (fd.type === 'select') {
                return (
                  <FormControl key={fd.key} fullWidth>
                    <InputLabel required={fd.required}>{fd.label}</InputLabel>
                    <Select value={val} label={fd.label} onChange={(e) => setVal(e.target.value)}>
                      {fd.options!.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                    </Select>
                  </FormControl>
                );
              }
              if (fd.type === 'text') {
                return (
                  <TextField key={fd.key} label={fd.label} value={val} required={fd.required} fullWidth
                    onChange={(e) => setVal(e.target.value)} />
                );
              }
              if (fd.type === 'pharmacy-search') {
                return (
                  <Autocomplete
                    key={fd.key}
                    freeSolo
                    options={pharmSearch.options}
                    loading={pharmSearch.loading}
                    inputValue={val}
                    getOptionLabel={(opt: any) =>
                      typeof opt === 'string'
                        ? opt
                        : `${opt.name}${opt.city ? ' — ' + opt.city : ''}${opt.address ? ', ' + opt.address : ''}`
                    }
                    onInputChange={(_e, newInput) => {
                      handlePharmSearch(newInput, setVal);
                    }}
                    onChange={(_e, selected) => {
                      if (selected && typeof selected !== 'string') {
                        const label = `${selected.name}${selected.city ? ' — ' + selected.city : ''}${selected.address ? ', ' + selected.address : ''}`;
                        setVal(label);
                        setPharmSearch({ options: [], loading: false });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={fd.label}
                        required={fd.required}
                        fullWidth
                        helperText="Type to search 798 Alberta pharmacies by name, city, or address"
                      />
                    )}
                  />
                );
              }
              if (fd.type === 'date') {
                return (
                  <TextField key={fd.key} label={fd.label} type="date" value={val}
                    InputLabelProps={{ shrink: true }} required={fd.required} fullWidth
                    onChange={(e) => setVal(e.target.value)} />
                );
              }
              return null;
            })}
        </Stack>
      </Box>
    );
  };

  const renderStep6 = () => {
    const toggleItem = (item: string, checked: boolean) =>
      setWizard((prev) => ({
        ...prev,
        counsellingItems: prev.counsellingItems.map((c) => c.item === item ? { ...c, checked } : c),
      }));

    return (
      <Box>
        <Typography variant="h6" gutterBottom>Counselling & Outcome</Typography>

        <Typography variant="subtitle2" gutterBottom>Counselling topics covered</Typography>
        {wizard.counsellingItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No drug-specific counselling points loaded — all drugs entered manually without DB match.
          </Typography>
        ) : (
          <FormGroup sx={{ mb: 2 }}>
            {wizard.counsellingItems.map((c) => (
              <FormControlLabel
                key={c.item}
                control={
                  <Checkbox size="small" checked={!!c.checked}
                    color={c.required ? 'error' : 'primary'}
                    onChange={(e) => toggleItem(c.item, e.target.checked)} />
                }
                label={
                  <Typography variant="body2">
                    {c.item}
                    {c.required && (
                      <Chip label="required" size="small" color="error"
                        sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} />
                    )}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        )}

        <Divider sx={{ my: 2 }} />

        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <FormLabel required>Patient understanding</FormLabel>
          <RadioGroup value={wizard.patientUnderstanding}
            onChange={(e) => update({ patientUnderstanding: e.target.value })} sx={{ mt: 0.5 }}>
            {PATIENT_UNDERSTANDING_OPTIONS.map((o) => (
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel required>Outcome</InputLabel>
          <Select value={wizard.outcome} label="Outcome" onChange={(e) => update({ outcome: e.target.value })}>
            {OUTCOME_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2">Follow-up required?</Typography>
          <Button size="small" variant={wizard.followUpRequired ? 'contained' : 'outlined'}
            onClick={() => update({ followUpRequired: true })}>Yes</Button>
          <Button size="small" variant={!wizard.followUpRequired ? 'contained' : 'outlined'}
            onClick={() => update({ followUpRequired: false })}>No</Button>
          {wizard.followUpRequired && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Timeframe</InputLabel>
              <Select value={wizard.followUpTimeframe} label="Timeframe"
                onChange={(e) => update({ followUpTimeframe: e.target.value })}>
                {FOLLOWUP_TIMEFRAMES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </Stack>

        <TextField
          label="Additional notes (optional)" multiline rows={3} fullWidth
          value={wizard.additionalNotes}
          onChange={(e) => update({ additionalNotes: e.target.value })}
          placeholder="Any additional clinical notes, exceptions, or observations..."
          helperText="This is the only free-text field in the wizard."
        />
      </Box>
    );
  };

  const renderStep7 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Generated DAP</Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <Button variant="contained" startIcon={<ContentCopy />} onClick={handleCopy}>
          Copy to clipboard
        </Button>
        <Button variant="outlined" startIcon={<FileDownload />} onClick={handleDownload}>
          Download .docx
        </Button>
      </Stack>
      <Paper
        variant="outlined"
        component="pre"
        sx={{
          p: 2,
          fontFamily: 'Courier New, monospace',
          fontSize: 13,
          lineHeight: 1.7,
          whiteSpace: 'pre-wrap',
          backgroundColor: '#f8f9fa',
          maxHeight: 520,
          overflowY: 'auto',
          m: 0,
        }}
      >
        {dapText}
      </Paper>
    </Box>
  );

  const stepContent = [
    renderStep1,
    renderStep2,
    renderStep3,
    renderStep4,
    renderStep5,
    renderStep6,
    renderStep7,
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>DAP Generator</Typography>
          <Typography variant="body2" color="text.secondary">
            Alberta College of Pharmacy — Pharmacist Clinical Documentation
          </Typography>
        </Box>
        {acpLicense && <Chip label={`ACP: ${acpLicense}`} color="primary" variant="outlined" />}
      </Box>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map((label, i) => (
          <Step key={label} completed={i < activeStep}>
            <StepLabel
              onClick={() => setActiveStep(i)}
              sx={{ cursor: 'pointer', '& .MuiStepLabel-label': { cursor: 'pointer' } }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={2} sx={{ p: 3, minHeight: 320 }}>
        {stepContent[activeStep]()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined" startIcon={<ArrowBack />}>
          Back
        </Button>
        <Button variant="text" color="warning" size="small" startIcon={<RestartAlt />} onClick={handleReset}>
          Start new
        </Button>
        {activeStep < STEPS.length - 1 && (
          <Button onClick={handleNext} variant="contained" disabled={!canAdvance()} endIcon={<ArrowForward />}>
            {activeStep === STEPS.length - 2 ? 'Generate DAP' : 'Next'}
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.msg}
        ContentProps={{ sx: { backgroundColor: snackbar.color === 'error' ? 'error.main' : 'success.main' } }}
      />
    </Box>
  );
};

export default PrescriptionProcessing;
